import { useEffect, useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTheme } from "@mui/material/styles";
import { SoftButton, SoftFooter, cls, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import { greenButtonSx, progressSx } from "../actionStyles";
import { HatimIcon } from "../../../components/ui/mdiIcons";
import GoalValueControl from "../../results/components/GoalValueControl";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useUpdateMyCompletedPageRangesMutation,
  useUpdateMyHatimProgressMutation,
  useLeaveHatimMutation,
} from "../../../api/hatimApi";
import { isRangeType } from "../../../lib/hatimType";
import { formatShortDate } from "../../../lib/dateUtils";
import {
  canEditInstance,
  isRangeSelected,
  rangesEqual,
  remainingLabel,
  sumPages,
  toggleRange,
} from "../../../lib/hatimEditing";

function statusTone(status) {
  if (status === "completed") return "bg-terakkiSuccess/10 text-terakkiSuccess";
  if (status === "pending") return "bg-terakkiSurfaceAlt text-terakkiNeutral";
  return "bg-terakkiBrandCloud text-terakkiPrimaryDark";
}

function statusKey(status) {
  if (status === "completed") return "hatim_card_completed_status";
  if (status === "pending") return "hatim_card_pending_status";
  return "hatim_card_in_progress_status";
}

function assignedRangesOf(instance) {
  const list = instance?.myAssignment?.assignedPageRanges;
  return Array.isArray(list) ? list : [];
}

function completedRangesOf(instance) {
  const list = instance?.myAssignment?.completedPageRanges;
  return Array.isArray(list) ? list : [];
}

function HatimItem({ instance, amountDraft, rangeDraft, onAmountChange, onToggleRange, onLeave, t, mode }) {
  const a = instance.myAssignment;
  const isRange = isRangeType(instance.type);
  const editable = canEditInstance(instance);
  const remaining = remainingLabel(instance.endDate, t);
  const finished = Boolean(remaining?.finished);

  const assignedRanges = assignedRangesOf(instance);
  const selectedRanges = rangeDraft ?? completedRangesOf(instance);

  const assigned = isRange ? sumPages(assignedRanges) : a?.assignedAmount || 0;
  const completed = isRange ? sumPages(selectedRanges) : amountDraft ?? a?.completedAmount ?? 0;
  const percent = assigned > 0 ? Math.min(100, Math.round((completed / assigned) * 100)) : 0;

  return (
    <li className="py-1" data-hatim={instance.id}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-base font-SemiBold text-terakkiInk leading-snug">{instance.name}</span>
        {/* Süresi dolmuş hatimde atama durumu ("Beklemede") yanıltıcıydı;
            onun yerine doğrudan süre bilgisini gösteriyoruz. */}
        {finished ? (
          <span
            data-chip="finished"
            className="text-[13px] px-2 py-0.5 rounded-full font-SemiBold shrink-0 bg-terakkiSurfaceAlt text-terakkiNeutral"
          >
            {t("hatim_finished")}
          </span>
        ) : (
          a && (
            <span className={`text-[13px] px-2 py-0.5 rounded-full font-SemiBold shrink-0 ${statusTone(a.status)}`}>
              {t(statusKey(a.status))}
            </span>
          )
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[13px] text-terakkiNeutral">
          {formatShortDate(instance.startDate)} – {formatShortDate(instance.endDate)}
        </span>
        {remaining && !finished && (
          <span className={`text-[13px] font-SemiBold ${remaining.tone}`}>{remaining.text}</span>
        )}
      </div>

      {!a ? (
        <p className="text-[13px] text-terakkiNeutral mt-1">{t("hatim_card_no_assignment")}</p>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-2">
            <LinearProgress
              variant="determinate"
              value={percent}
              className="flex-1 !h-1.5 !rounded-full"
              sx={progressSx(mode)}
            />
            <span className="text-[13px] text-terakkiNeutral whitespace-nowrap">
              {completed} / {assigned} {isRange ? t("hatim_pages_unit") : t("hatim_card_assigned_amount")}
            </span>
          </div>

          {!editable ? (
            <p className="flex items-center gap-1.5 text-[13px] text-terakkiNeutral mt-2" data-locked>
              {instance.isCompleted ? (
                <CheckCircleIcon sx={{ fontSize: 14 }} aria-hidden />
              ) : (
                <LockOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
              )}
              {instance.isCompleted ? t("hatim_locked_completed") : t("hatim_locked_passed")}
            </p>
          ) : isRange ? (
            // Mobildeki PageRangePicker gibi: atanmış aralıklar doğrudan burada
            // seçiliyor, ayrı bir düzenleme ekranına gitmeye gerek yok.
            <div className="mt-2">
              <p className="text-[13px] text-terakkiNeutral mb-1.5">{t("hatim_pick_ranges")}</p>
              {assignedRanges.length === 0 ? (
                <p className="text-[13px] text-terakkiNeutral">{t("hatim_no_assigned_ranges")}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {assignedRanges.map((range) => {
                    const active = isRangeSelected(selectedRanges, range);
                    return (
                      <button
                        key={`${range.startPage}-${range.endPage}`}
                        type="button"
                        data-range={`${range.startPage}-${range.endPage}`}
                        aria-pressed={active}
                        onClick={() => onToggleRange(instance, range)}
                        className={[
                          "px-2.5 py-1 rounded-full text-[13px] font-SemiBold border transition-colors",
                          active
                            ? "bg-terakkiPrimaryDark text-white border-terakkiPrimaryDark"
                            : "bg-terakkiSurfaceAlt text-terakkiInk border-terakkiBorder hover:border-terakkiPrimary",
                        ].join(" ")}
                      >
                        {range.startPage}–{range.endPage}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <GoalValueControl
                value={completed}
                min={0}
                max={assigned || 9999}
                onChange={(next) => onAmountChange(instance.id, next)}
                onComplete={assigned > 0 ? () => onAmountChange(instance.id, assigned) : undefined}
              />
            </div>
          )}
        </>
      )}

      {/* Katılımcı hatimden kendi isteğiyle ayrılabilir (mobildeki Hatimden Çık) */}
      {a && instance.template ? (
        <div className="flex justify-end mt-1.5">
          <button
            type="button"
            onClick={() => onLeave(instance)}
            data-leave-hatim={instance.id}
            className="inline-flex items-center gap-1 text-[13px] text-terakkiNeutral hover:text-terakkiError transition-colors"
          >
            <LogoutIcon sx={{ fontSize: 13 }} aria-hidden />
            {t("Hatimden ayrıl")}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** Mobil ajandayla aynı kural: hatim yalnızca başlangıç–bitiş aralığındaki günlerde görünür. */
export function coversDay(instance, localDate) {
  if (!localDate) return true;
  const start = String(instance?.startDate || "").slice(0, 10);
  const end = String(instance?.endDate || "").slice(0, 10);
  if (!start || !end) return false;
  return localDate >= start && localDate <= end;
}

/**
 * Daire şeridinde basılan hatimin penceresi (mobil HatimProgressModal).
 * İsim, kalan süre ve payımın ilerlemesi; aynı kuralla (aktif +
 * tamamlanmamış) düzenleme: adet tipinde artı/eksi, sayfa aralıklı tipte
 * aralık seçimi. Katılımcı buradan hatimden ayrılabilir.
 */
export default function HatimDialog({ instance, onClose }) {
  const { t } = useScriptMode();
  const mode = useTheme().palette.mode;
  const toast = useToast();
  const paperSx = useDialogPaperSx();

  const [updateProgress, { isLoading: savingAmount }] = useUpdateMyHatimProgressMutation();
  const [updateRanges, { isLoading: savingRanges }] = useUpdateMyCompletedPageRangesMutation();
  const [leaveHatim, { isLoading: leaveLoading }] = useLeaveHatimMutation();
  const [leaving, setLeaving] = useState(null);
  const [amountDraft, setAmountDraft] = useState(null);
  const [rangeDraft, setRangeDraft] = useState(null);

  // Başka bir hatim açılınca ya da sunucudan yeni veri gelince taslak sıfırlanır.
  useEffect(() => {
    setAmountDraft(null);
    setRangeDraft(null);
  }, [instance]);

  const saving = savingAmount || savingRanges;
  const editable = Boolean(instance && canEditInstance(instance) && instance.myAssignment);
  const isRange = instance ? isRangeType(instance.type) : false;
  const amountChanged =
    editable && !isRange && amountDraft != null && amountDraft !== (instance.myAssignment.completedAmount || 0);
  const rangesChanged =
    editable && isRange && rangeDraft && !rangesEqual(rangeDraft, completedRangesOf(instance));
  const hasChanges = Boolean(amountChanged || rangesChanged);

  const handleToggleRange = (inst, range) => {
    const current = rangeDraft ?? completedRangesOf(inst);
    setRangeDraft(toggleRange(current, range, sumPages(assignedRangesOf(inst))));
  };

  const handleSave = async () => {
    const result = amountChanged
      ? await updateProgress({ instanceId: String(instance.id), completedAmount: Number(amountDraft) })
      : await updateRanges({
          instanceId: String(instance.id),
          ranges: rangeDraft.map((r) => ({ startPage: Number(r.startPage), endPage: Number(r.endPage) })),
        });
    if (result.error) {
      toast.error(result.error.message || t("hatim_mgmt_action_error"));
      return;
    }
    toast.success(t("results_save_success"));
    onClose();
  };

  const confirmLeave = async () => {
    if (!leaving) return;
    const result = await leaveHatim({ hatimTemplateId: String(leaving.template) });
    if (result.error) {
      toast.error(result.error.message || t("Hatimden ayrılınamadı."));
    } else {
      toast.success(t("Hatimden ayrıldınız."));
      onClose();
    }
    setLeaving(null);
  };

  return (
    <>
      <Dialog
        open={!!instance}
        onClose={saving ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: paperSx }}
      >
        <div className="flex items-center gap-3 px-5 pt-4 pb-2">
          <span className="w-10 h-10 rounded-full bg-terakkiPrimaryDark text-white flex items-center justify-center shrink-0">
            <HatimIcon sx={{ fontSize: 20 }} aria-hidden />
          </span>
          <div className={`flex-1 min-w-0 text-[15px] font-medium ${cls.title}`}>{t("hatim_my_title")}</div>
          <IconButton size="small" onClick={onClose} disabled={saving} aria-label={t("close")}>
            <CloseIcon fontSize="small" className={cls.muted} />
          </IconButton>
        </div>
        <DialogContent sx={{ pt: 1 }}>
          {instance ? (
            <ul>
              <HatimItem
                instance={instance}
                amountDraft={amountDraft ?? undefined}
                rangeDraft={rangeDraft ?? undefined}
                onAmountChange={(id, next) => setAmountDraft(next)}
                onToggleRange={handleToggleRange}
                onLeave={setLeaving}
                t={t}
                mode={mode}
              />
            </ul>
          ) : null}
        </DialogContent>
        {editable ? (
          <SoftFooter>
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={!hasChanges}
              onClick={handleSave}
              fullWidth
              sx={greenButtonSx(mode)}
            >
              {hasChanges ? t("results_save") : t("calendar_saved")}
            </LoadingButton>
          </SoftFooter>
        ) : null}
      </Dialog>

      <Dialog
        open={!!leaving}
        onClose={leaveLoading ? undefined : () => setLeaving(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={{ fontWeight: 500 }}>{t("Hatimden ayrıl")}</DialogTitle>
        <DialogContent>
          <p className="text-base text-terakkiNeutral dark:text-white/70">
            {t(
              "{name} hatminden ayrılmak istediğinize emin misiniz? Size atanan bölüm boşta kalacak ve hatim yöneticilerine bildirim gidecek.",
              { name: leaving?.name || "" }
            )}
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton tone="cancel" onClick={() => setLeaving(null)} disabled={leaveLoading}>
            {t("Vazgeç")}
          </SoftButton>
          <SoftButton tone="danger" onClick={confirmLeave} disabled={leaveLoading}>
            {t("Hatimden ayrıl")}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
